import type { MedicationsVaccinesState } from "./MedicationsVaccinesContext";
import { RequestBody } from "@src/packages/openapi/helpers";
import { paths } from "@src/packages/openapi/schema";

function formatDate(date: Date | null | undefined) {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function transformMedicationsVaccinesStateToRequest(
  state: MedicationsVaccinesState,
): RequestBody<paths["/{tenantId}/patients/{patientId}"]["patch"]> {
  const currentlyTaking =
    state.currentlyTakingMedications ??
    (state.selectedProducts.length > 0 &&
      !state.selectedProducts.includes("none"));

  // Track medications to delete from individual removals
  const individuallyDeletedMedications = (
    state.medicationIdsToDelete || []
  ).map((id) => {
    // Try to find the medication to get its name
    const med = state.medicationDetails.find((m) => m.id === id);
    return {
      id,
      name: med?.name || "",
      op: "delete" as const,
    };
  });

  // When currentlyTaking is false, mark all existing medications (those with id) for deletion
  const medicationsToDeleteFromNo = !currentlyTaking
    ? state.medicationDetails
        .filter((m) => !!m.id)
        .map((m) => ({
          id: m.id!,
          name: m.name || "",
          op: "delete" as const,
        }))
    : [];

  // Combine both deletion sources, avoiding duplicates
  const allMedicationIdsToDelete = new Set([
    ...individuallyDeletedMedications.map((m) => m.id),
    ...medicationsToDeleteFromNo.map((m) => m.id),
  ]);

  const medicationsToDelete = Array.from(allMedicationIdsToDelete).map((id) => {
    // Find the medication to get its name
    const med =
      state.medicationDetails.find((m) => m.id === id) ||
      individuallyDeletedMedications.find((m) => m.id === id);
    return {
      id,
      name: med?.name || "",
      op: "delete" as const,
    };
  });

  // Exclude deleted medications from the details array
  const medicationIdsToDeleteSet = new Set(state.medicationIdsToDelete || []);

  return {
    medicalHistory: {
      currentlyTakingMedications: currentlyTaking,
      medications: [
        ...(currentlyTaking
          ? state.medicationDetails
              .filter(
                (detail) =>
                  !medicationIdsToDeleteSet.has(detail.id || "") &&
                  !!detail.name?.trim(),
              )
              .map((detail) => ({
                id: detail.id,
                name: detail.name!.trim(),
                brand: detail.brand ?? null,
                frequency: detail.frequency ?? null,
                notes: detail.notes ?? null,
              }))
          : []),
        ...medicationsToDelete,
      ],
      vaccinations: (() => {
        // Track vaccines to delete from individual removals
        const individuallyDeletedVaccines = (
          state.vaccineIdsToDelete || []
        ).map((id) => ({
          id,
          type: "other" as const,
          op: "delete" as const,
        }));

        if (state.selectedVaccines[0] === "none") {
          // When "none" selected, delete all existing vaccines
          const vaccinesToDeleteFromNo = state.vaccineDetails
            .filter((detail) => detail.id)
            .map((detail) => ({
              id: detail.id,
              type: detail.vaccine ?? "other",
              op: "delete" as const,
            }));

          // Combine and dedupe
          const allVaccineIdsToDelete = new Set([
            ...individuallyDeletedVaccines.map((v) => v.id),
            ...vaccinesToDeleteFromNo.map((v) => v.id),
          ]);

          return Array.from(allVaccineIdsToDelete).map((id) => ({
            id,
            type: "other" as const,
            op: "delete" as const,
          }));
        }

        // Normal case: include active vaccines + individually deleted ones
        return [
          ...state.vaccineDetails.map((detail) => ({
            id: detail.id,
            type: detail.vaccine ?? "other",
            brand: detail.brand ?? null,
            dateReceived: formatDate(detail.date),
            notes: detail.notes ?? null,
            other:
              detail.vaccine === "other" && detail.notes ? detail.notes : null,
          })),
          ...individuallyDeletedVaccines,
        ];
      })(),
    },
  };
}
